import { useSelector } from '@/api/state';
import SupplierHeader from '@/ui/components/supplierHeader/SupplierHeader';
import SupplierSupplierMaterials from '@/ui/components/supplierSupplierMaterials/SupplierSupplierMaterials';
import { getSupplier } from '@/utils/stateAccessors';
import { Card } from '@payaca/ambrosia';
import RouterTabs from '@payaca/components/routerTabs/RouterTabs';

import { requestGetSupplier } from '@payaca/store/suppliers/suppliersActions';
import { FC, useEffect, useMemo, type JSX } from 'react';

import { useDispatch } from 'react-redux';

import { usePermissions } from '@/hooks/usePermissions';
import SupplierPurchaseOrders from '@/ui/components/supplierPurchaseOrders/SupplierPurchaseOrders';
import { MaterialsListPermissions } from '@payaca/permissions/materialsList/materialsList.permissions';
import { useLocation } from 'react-router';

import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';

type Props = {
  supplierId: number;
};

const SupplierPage: FC<Props> = ({ supplierId }: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const { userHasRequiredPermission } = usePermissions();
  const location = useLocation();

  useEffect(() => {
    dispatch(requestGetSupplier(supplierId));
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [supplierId]);

  const showPurchaseOrdersTab = userHasRequiredPermission([
    MaterialsListPermissions.GET_PURCHASE_ORDERS,
  ]);

  const routerTabPanels = useMemo(() => {
    const t = [
      {
        slug: 'materials',
        label: 'Materials',
        render: () => (
          <SupplierSupplierMaterials
            supplierId={supplierId}
            onSupplierUpdateSuccess={() =>
              dispatch(requestGetSupplier(supplierId))
            }
          />
        ),
      },
    ];

    if (showPurchaseOrdersTab) {
      t.push({
        slug: 'purchase-orders',
        label: 'Purchase orders',
        render: () => <SupplierPurchaseOrders supplierId={supplierId} />,
      });
    }

    return t;
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: location.hash
  }, [supplierId, dispatch, location.hash, showPurchaseOrdersTab]);

  const supplier = useSelector((state) => getSupplier(state, supplierId));

  return (
    <AuthenticatedPageWrapper
      breadcrumbItems={[
        { label: 'Suppliers', to: '/suppliers' },
        { label: supplier?.name || '' },
      ]}
    >
      <RouterTabs panels={routerTabPanels}>
        <div className="flex flex-col p-6 pb-4">
          <Card>
            <Card.Body>
              <div className="mb-4">
                <SupplierHeader supplierId={supplierId} />
              </div>

              <RouterTabs.Nav
                className="-mb-4 md:-mb-5"
                defaultTabSlug="materials"
              />
            </Card.Body>
          </Card>
        </div>

        <div className="p-6 pt-0">
          <RouterTabs.Panels />
        </div>
      </RouterTabs>
    </AuthenticatedPageWrapper>
  );
};

export default SupplierPage;
